/* eslint-disable */

import { StatSummary } from '../api/types/lambda-graph-summaries-request';

function parseYYYYMMDD(value: string): Date | null {
	if (!/^\d{8}$/.test(value)) return null;

	const year = parseInt(value.slice(0, 4), 10);
	const month = parseInt(value.slice(4, 6), 10) - 1;
	const day = parseInt(value.slice(6, 8), 10);

	if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

	return new Date(Date.UTC(year, month, day));
}

function parseMonthYear(value: string): Date | null {
	// Strict match for formats like "Dec 2024", "January 2023"
	if (!/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}$/i.test(value)) {
		return null;
	}

	const parsed = new Date(Date.parse(value));
	return isNaN(parsed.getTime()) ? null : parsed;
}

export function summarizeDynamicData(
	data: any[],
	entityName: string,
	graphId: string
): StatSummary {
	if (!data || data.length === 0) {
		return {
			graphId,
			entityName,
			top3: [],
			overallMin: 0,
			overallMax: 0,
			total: 0,
			mean: 0,
			stdDev: 0,
			LatestDate: undefined,
		};
	}

	try {
		const values: number[] = [];
		const topPairs: { category: string; series: string; value: number }[] = [];
		const dateCandidates: { date: Date; original: string }[] = [];

		for (const row of data) {
			const keys = Object.keys(row);
			const categoryKey = keys[0];
			const categoryValue = row[categoryKey];

			for (let i = 0; i < keys.length; i++) {
				const seriesKey = keys[i];
				const rawValue = row[seriesKey];

				// Try YYYYMMDD
				const ymdDate = parseYYYYMMDD(rawValue);
				if (ymdDate) {
					dateCandidates.push({
						date: ymdDate,
						original: ymdDate.toISOString().slice(0, 10),
					});
					continue;
				}

				// Try "MMM YYYY"
				if (typeof rawValue === 'string') {
					const monthYearDate = parseMonthYear(rawValue);
					if (monthYearDate) {
						dateCandidates.push({ date: monthYearDate, original: rawValue });
						continue;
					}
				}

				// Handle number values
				const numValue = Number(rawValue);
				if (!isNaN(numValue)) {
					values.push(numValue);
					topPairs.push({
						category: categoryValue,
						series: seriesKey,
						value: numValue,
					});
				}
			}
		}

		const top3 = [...topPairs].sort((a, b) => b.value - a.value).slice(0, 3);
		const total = values.reduce((acc, val) => acc + val, 0);
		const mean = total / values.length;
		const variance =
			values.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / values.length;
		const stdDev = Math.sqrt(variance);
		const overallMin = Math.min(...values);
		const overallMax = Math.max(...values);

		const LatestDate =
			dateCandidates.length > 0
				? dateCandidates.sort((a, b) => b.date.getTime() - a.date.getTime())[0].original
				: undefined;

		return {
			graphId,
			entityName,
			top3,
			overallMin,
			overallMax,
			total,
			mean,
			stdDev,
			LatestDate,
		};
	} catch {
		return {
			graphId,
			entityName: `Chart type from chart:${entityName} not supported`,
			top3: [],
			overallMin: 0,
			overallMax: 0,
			total: 0,
			mean: 0,
			stdDev: 0,
			LatestDate: undefined,
		};
	}
}

// Chart ids and report ids are independent sequences; unprefixed they can
// collide on one dashboard and clobber each other's stat entries.
export function chartGraphId(id: number): string {
	return `chart-${id}`;
}

export function reportGraphId(id: number): string {
	return `report-${id}`;
}

export function widgetGraphId(widget: {
	chart?: number | null;
	report?: number | null;
}): string | undefined {
	if (widget.chart) return chartGraphId(widget.chart);
	if (widget.report) return reportGraphId(widget.report);
	return undefined;
}

// One current StatSummary per widget — stale entries feed the insights payload.
export function replaceGraphSummaryStat(prev: StatSummary[], stat: StatSummary): StatSummary[] {
	return [...prev.filter((s) => s.graphId !== stat.graphId), stat];
}

// A widget whose retrieval returned no rows must lose its previous entry too,
// or the payload keeps quoting data that is no longer on the dashboard.
export function removeGraphSummaryStat(prev: StatSummary[], graphId: string): StatSummary[] {
	return prev.filter((s) => s.graphId !== graphId);
}
